import React, { ReactNode, useMemo, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  alpha,
  Drawer as MuiDrawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Box,
  Theme,
  Typography,
} from '@mui/material';
import { IconButton } from '@components/iconButton';
import { ChevronLeft, ChevronRight, Cog } from '@assets/svgs';
import { arraysOverlap } from '@utils/arrays';
import { isModifiedClick } from '@utils/clickEvents';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';
import { Tooltip } from '@components/tooltip/Tooltip';

export type AdminDrawerListItem = {
  displayName?: string;
  name: string;
  path?: string;
  roles: string[];
  isDisabled?: boolean;
};

export type AdminDrawerSection = {
  displayName?: string;
  items: AdminDrawerListItem[];
  name: string;
};

interface IAdminDrawer {
  children?: ReactNode;
  items?: AdminDrawerListItem[];
  sections?: AdminDrawerSection[];
  toggleDrawer?: (name: string | null) => void;
}

const COLLAPSED_WIDTH = 64;
const EXPANDED_WIDTH = 248;
const DRILLDOWN_WIDTH = 280;

export const AdminDrawer = React.memo<IAdminDrawer>(({ children, items, sections, toggleDrawer }) => {
  const [roles] = useStore((state) => [state.roles], shallow);
  const [isAdminNavExpanded, setIsAdminNavExpanded] = useState(true);

  const location = useLocation();

  const visibleSections = useMemo(
    () =>
      (sections ?? [{ items: items ?? [], name: 'Portal Admin' }])
        .map((section) => ({
          ...section,
          items: section.items.filter((item) => arraysOverlap(item.roles, roles)),
        }))
        .filter((section) => section.items.length > 0),
    [items, roles, sections],
  );

  // Items with a path render as a real link (see below), so the browser already handles plain
  // navigation as well as ctrl/cmd/shift/middle-click "open in new tab" behavior on its own. This
  // handler only needs to run the `toggleDrawer` side effect, and only for a plain left-click —
  // otherwise a modified click meant to open a new tab would also navigate the current one.
  const handleItemClick = (item: AdminDrawerListItem, event: React.MouseEvent) => {
    if (item.isDisabled || !toggleDrawer) return;
    if (item.path && isModifiedClick(event)) return;

    toggleDrawer(item.name);
  };

  const handleToggleExpansion = () => {
    setIsAdminNavExpanded(!isAdminNavExpanded);
  };

  const currentWidth = children ? DRILLDOWN_WIDTH : isAdminNavExpanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH;
  const drawerTransition = children ? 'none' : 'width 0.2s ease-in-out';

  const getIsSelected = (item: AdminDrawerListItem) => !!item.path && location.pathname.includes(item.path);
  const activeSection = visibleSections.find((section) => section.items.some(getIsSelected));
  const activeItem = activeSection?.items.find(getIsSelected);
  const activeLabel = activeItem?.displayName ?? activeItem?.name ?? activeSection?.displayName ?? activeSection?.name;

  return (
    <Box
      sx={{
        position: 'relative',
        width: `${currentWidth}px`,
        minWidth: `${currentWidth}px`,
        display: 'flex',
        flexDirection: 'row',
        transition: drawerTransition,
        height: '100%',
        flexShrink: 0,
      }}
    >
      <MuiDrawer
        variant='permanent'
        anchor='left'
        slotProps={{
          paper: {
            sx: {
              backgroundColor: 'background.paper',
              top: 'unset !important',
              height: '100%',
              width: `${currentWidth}px`,
              borderRadius: '0',
              boxShadow: 'none',
              position: 'relative',
              left: 0,
              right: 0,
              zIndex: 1,
              // Only the nav list scrolls (below), so the "Portal Admin" header and collapse button stay put.
              overflow: 'hidden',
              transition: drawerTransition,
              border: 'none',
              borderRight: '1px solid',
              borderColor: 'divider',
            },
          },
        }}
      >
        {children ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>{children}</Box>
        ) : (
          <>
            <Box
              sx={{
                alignItems: 'center',
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                flexShrink: 0,
                justifyContent: isAdminNavExpanded ? 'space-between' : 'center',
                minHeight: 60,
                px: isAdminNavExpanded ? 1.5 : 1,
                py: 1.25,
              }}
            >
              {isAdminNavExpanded && (
                <Typography
                  variant='bodySmall'
                  component={RouterLink}
                  to='/portal-admin'
                  sx={{
                    color: 'text.main',
                    fontSize: 13,
                    fontWeight: 600,
                    letterSpacing: 0,
                    lineHeight: 1,
                    textDecoration: 'none',
                    textTransform: 'none',
                    '&:hover': {
                      color: 'secondary.main',
                    },
                  }}
                >
                  Portal Admin
                </Typography>
              )}
              <Tooltip
                enterDelay={1000}
                enterNextDelay={1000}
                title={isAdminNavExpanded ? 'Collapse Portal Admin navigation' : 'Expand Portal Admin navigation'}
              >
                <IconButton
                  aria-label={
                    isAdminNavExpanded ? 'Collapse Portal Admin navigation' : 'Expand Portal Admin navigation'
                  }
                  onClick={handleToggleExpansion}
                  size='small'
                  variant='rounded'
                  sx={{
                    // on dark palettes sit on the title area's own color with an accent outline, like the back button
                    backgroundColor: (theme: Theme) =>
                      theme.palette.theme === 'lio' ? 'background.main' : 'transparent',
                    border: '1px solid',
                    borderColor: (theme: Theme) => (theme.palette.theme === 'lio' ? 'divider' : 'secondary.main'),
                    color: 'secondary.main',
                    height: 36,
                    width: 36,
                    '&:hover': {
                      backgroundColor: (theme: Theme) =>
                        theme.palette.theme === 'lio' ? 'background.main' : alpha(theme.palette.secondary.main, 0.12),
                    },
                  }}
                >
                  {isAdminNavExpanded ? (
                    <ChevronLeft width={16} height={16} />
                  ) : (
                    <ChevronRight width={16} height={16} />
                  )}
                </IconButton>
              </Tooltip>
            </Box>

            <List
              component='nav'
              aria-label='Portal Admin navigation'
              sx={{
                flex: 1,
                margin: 0,
                minHeight: 0,
                overflowX: 'hidden',
                overflowY: 'auto',
                px: isAdminNavExpanded ? 1.25 : 1,
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': {
                  display: 'none',
                },
              }}
            >
              {isAdminNavExpanded ? (
                visibleSections.map((section, sectionIndex) => (
                  <Box
                    key={section.name}
                    sx={{
                      mt: sectionIndex === 0 ? 0 : 1.75,
                    }}
                  >
                    <Box
                      sx={{
                        alignItems: 'center',
                        display: 'flex',
                        mb: 0.5,
                        minHeight: 24,
                      }}
                    >
                      {/* Teal display font so section labels read apart from the links beneath them. */}
                      <Typography
                        sx={{
                          color: 'secondary.main',
                          flex: 1,
                          fontFamily: 'Sofia Pro',
                          fontSize: 11,
                          fontWeight: 600,
                          letterSpacing: 0.5,
                          lineHeight: 1.2,
                          px: 1,
                          textTransform: 'uppercase',
                        }}
                      >
                        {section.displayName ?? section.name}
                      </Typography>
                    </Box>

                    {section.items.map((item) => {
                      const isSelected = getIsSelected(item);
                      const isLink = !!item.path && !item.isDisabled;

                      const itemButtonSx = {
                        backgroundColor: 'transparent',
                        borderLeft: '3px solid',
                        borderColor: isSelected ? 'secondary.main' : 'transparent',
                        borderRadius: 0,
                        color: isSelected ? 'secondary.main' : 'text.main',
                        minHeight: 32,
                        px: 1,
                        py: 0.625,
                        textDecoration: 'none',
                        '&:hover': {
                          backgroundColor: (theme: Theme) => alpha(theme.palette.secondary.main, 0.08),
                        },
                        '&.Mui-disabled': {
                          opacity: 0.5,
                        },
                      };

                      const itemContent = (
                        <ListItemText
                          primary={item.displayName ?? item.name}
                          sx={{
                            m: 0,
                            '& .MuiListItemText-primary': {
                              color: 'inherit',
                              fontSize: 12.5,
                              fontWeight: isSelected ? 600 : 500,
                              letterSpacing: 0,
                              lineHeight: 1.25,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            },
                          }}
                        />
                      );

                      return (
                        <ListItem key={item.name} disablePadding sx={{ display: 'block', mb: 0.25 }}>
                          {isLink ? (
                            <ListItemButton
                              component={RouterLink}
                              to={item.path as string}
                              onClick={(event) => handleItemClick(item, event)}
                              sx={itemButtonSx}
                            >
                              {itemContent}
                            </ListItemButton>
                          ) : (
                            <ListItemButton
                              disabled={item.isDisabled}
                              onClick={(event) => handleItemClick(item, event)}
                              sx={itemButtonSx}
                            >
                              {itemContent}
                            </ListItemButton>
                          )}
                        </ListItem>
                      );
                    })}
                  </Box>
                ))
              ) : activeLabel ? (
                <Box
                  sx={{
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    pt: 2,
                  }}
                >
                  <Tooltip placement='right' title={`Current page: ${activeLabel}`}>
                    <Box
                      aria-label={`Current Portal Admin page: ${activeLabel}`}
                      sx={{
                        alignItems: 'center',
                        backgroundColor: 'button.outlinedDark',
                        borderRadius: '50%',
                        color: 'button.outlined',
                        display: 'flex',
                        height: 38,
                        justifyContent: 'center',
                        margin: '0 auto',
                        width: 38,
                        '& svg': {
                          color: 'inherit',
                          height: 18,
                          width: 18,
                        },
                      }}
                    >
                      <Cog aria-hidden='true' />
                    </Box>
                  </Tooltip>
                </Box>
              ) : null}
            </List>
          </>
        )}
      </MuiDrawer>
    </Box>
  );
});

AdminDrawer.displayName = 'AdminDrawer';
